import React, { useEffect, useRef, useState } from 'react';
import { api, createTopic, updateTopic, uploadFile } from '../lib/apiClient';
import { normalizeEmbedUrl } from '../lib/embedNormalize';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pull the filename out of a self-hosted video URL like `${API}/api/files/abc.mp4`
const selfHostedFilename = (url) => {
  if (!url) return null;
  const m = /\/api\/files\/([^?#/]+\.(mp4|webm|mov))(\?|$)/i.exec(url);
  return m ? m[1] : null;
};

const VideoTranscribePanel = ({ videoUrl }) => {
  const filename = selfHostedFilename(videoUrl);
  const [status, setStatus] = useState(null);   // {transcript, segments_count, extracted_at}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setStatus(null); setError('');
    if (!filename) return;
    api.get(`/api/files/${filename}/transcript`)
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [filename]);

  if (!filename) return null;   // only for self-hosted videos

  const transcribe = async () => {
    if (!window.confirm(
      'Transcribe this video with OpenAI Whisper?\n\n' +
      '• Audio will be auto-extracted (mono 16 kHz MP3) so videos of any size work — ' +
      'up to ~2 hours per file.\n' +
      '• Cost: ~$0.006 per minute of audio, billed to your Emergent Universal Key ' +
      '(a 20-min lecture ≈ $0.12).\n' +
      '• The transcript with timestamps will be fed into the AI Tutor for grounded answers.\n\n' +
      'Continue?'
    )) return;
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/api/files/${filename}/transcribe`);
      // refresh status
      const r = await api.get(`/api/files/${filename}/transcript`);
      setStatus(r.data);
      alert(`Transcribed: ${data.characters.toLocaleString()} chars, ${data.segments} segments.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Transcription failed');
    } finally { setBusy(false); }
  };

  const hasTranscript = !!(status && status.transcript);

  return (
    <div className="mt-2 p-2 rounded text-xs flex items-center gap-2 flex-wrap"
         style={{ background: 'var(--unthsc-green-light)' }} data-testid="video-transcribe-panel">
      <span>🎙</span>
      {hasTranscript ? (
        <span className="text-gray-800">
          Transcript ready — <span className="font-semibold">{status.segments_count}</span> segments.
          The AI Tutor will use this when answering questions about this lecture.
        </span>
      ) : (
        <span className="text-gray-800">
          No transcript yet. Transcribe so the AI Tutor can answer questions grounded in this video.
        </span>
      )}
      <button onClick={transcribe} disabled={busy}
              className="btn-secondary text-xs py-1 px-2 ml-auto"
              data-testid="transcribe-video-btn">
        {busy ? 'Transcribing…' : (hasTranscript ? 'Re-transcribe' : 'Transcribe video')}
      </button>
      {error && <div className="w-full text-red-700">{error}</div>}
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
    {hint && <div className="text-xs text-gray-500 mb-1">{hint}</div>}
    {children}
  </div>
);

const UploadButton = ({ accept, label, onUploaded, disabled }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const handleChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const data = await uploadFile(f, setProgress);
      onUploaded(`${API_URL}${data.url}`, data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={handleChange} />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={disabled || uploading}
              className="btn-secondary text-xs py-1.5 px-3">
        {uploading ? `Uploading ${progress}%…` : `Upload ${label}`}
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
};

const empty = {
  title: '',
  summary: '',
  presentation_url: '',
  video_url: '',
  assignment_text: '',
  assignment_url: '',
  lab_text: '',
  lab_url: '',
  discussion_text: '',
  discussion_url: '',
  references_text: '',
  references_url: '',
  quiz_url: '',
  order: 0,
};

const TopicEditModal = ({ moduleId, topic, onClose, onSaved }) => {
  const isNew = !topic?.id;
  const [form, setForm] = useState({ ...empty, ...(topic || {}) });
  const [hints, setHints] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleUrlChange = (k, kind) => (e) => {
    const raw = e.target.value;
    set(k, raw);
    const { url, hint } = normalizeEmbedUrl(raw, kind);
    setHints((h) => ({ ...h, [k]: hint }));
    if (url && url !== raw) set(k, url);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Topic title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const final = { ...form };
      ['video_url', 'presentation_url', 'quiz_url', 'assignment_url', 'lab_url', 'discussion_url', 'references_url'].forEach((k) => {
        const kind = k === 'quiz_url' ? 'quiz' : k === 'video_url' ? 'video' : 'pdf';
        const { url } = normalizeEmbedUrl(final[k], kind);
        final[k] = url || '';
      });
      if (isNew) {
        await createTopic(moduleId, final);
      } else {
        await updateTopic(moduleId, topic.id, final);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="topic-edit-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="text-xl font-bold text-gray-900">
            {isNew ? 'New Topic' : `Edit Topic · ${topic.title}`}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
                  data-testid="topic-edit-close-btn">×</button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
          )}

          <Field label="Topic title" hint="Free-form (e.g., 'Introduction to AI', '1.2 Big Data in Healthcare').">
            <input className="input-field" value={form.title} onChange={(e) => set('title', e.target.value)}
                   data-testid="topic-edit-title-input" placeholder="Topic title" />
          </Field>

          <Field label="Summary" hint="Rich text — headings, lists, links, images.">
            <RichTextEditor value={form.summary} onChange={(html) => set('summary', html)}
                            placeholder="Write the topic summary…" testId="topic-edit-summary-rte" />
          </Field>

          <Field label="Presentation (PDF URL)" hint="Public PDF URL or upload a file (always renders).">
            <input className="input-field" value={form.presentation_url}
                   onChange={handleUrlChange('presentation_url', 'pdf')}
                   placeholder="https://…/slides.pdf" />
            {hints.presentation_url && <div className="text-xs text-amber-700 mt-1">{hints.presentation_url}</div>}
            <UploadButton accept="application/pdf" label="PDF"
                          onUploaded={(url) => { set('presentation_url', url); }} />
          </Field>

          <Field label="Video (OneDrive/GDrive/YouTube embed URL)"
                 hint="Use the EMBED URL (Share → Embed → iframe src), or upload an MP4 below.">
            <input className="input-field" value={form.video_url}
                   onChange={handleUrlChange('video_url', 'video')}
                   placeholder="https://…/preview  or  https://www.youtube.com/embed/…" />
            {hints.video_url && <div className="text-xs text-amber-700 mt-1">{hints.video_url}</div>}
            <UploadButton accept="video/mp4,video/webm,video/quicktime" label="Video"
                          onUploaded={(url) => { set('video_url', url); }} />
            <VideoTranscribePanel videoUrl={form.video_url} />
          </Field>

          <Field label="Assignment notes (optional)" hint="Rich text with links — like Markdown but visual.">
            <RichTextEditor value={form.assignment_text} onChange={(html) => set('assignment_text', html)}
                            placeholder="Optional — assignment instructions, references…"
                            testId="topic-edit-assignment-rte" />
          </Field>

          <Field label="Assignment URL (optional)" hint="Word/PowerPoint/Excel docs are auto-rendered inline.">
            <input className="input-field" value={form.assignment_url}
                   onChange={handleUrlChange('assignment_url', 'pdf')}
                   placeholder="https://…/assignment.docx" />
            {hints.assignment_url && <div className="text-xs text-amber-700 mt-1">{hints.assignment_url}</div>}
            <UploadButton accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.md" label="Assignment File"
                          onUploaded={(url) => { set('assignment_url', url); }} />
          </Field>

          <Field label="Lab notes (optional)" hint="Rich text with links — lab instructions, datasets, code snippets.">
            <RichTextEditor value={form.lab_text} onChange={(html) => set('lab_text', html)}
                            placeholder="Optional — lab instructions, links to notebooks/datasets…"
                            testId="topic-edit-lab-rte" />
          </Field>

          <Field label="Lab URL (optional)" hint="Notebook URL, GitHub repo, dataset, etc.">
            <input className="input-field" value={form.lab_url}
                   onChange={handleUrlChange('lab_url', 'pdf')}
                   placeholder="https://github.com/… or https://colab.research.google.com/…" />
            <UploadButton accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.md" label="Lab File"
                          onUploaded={(url) => { set('lab_url', url); }} />
          </Field>

          <Field label="Discussion Board notes (optional)"
                 hint="Brief intro / discussion prompt, with a link to the external forum.">
            <RichTextEditor value={form.discussion_text} onChange={(html) => set('discussion_text', html)}
                            placeholder="Optional — discussion prompt, posting guidelines…"
                            testId="topic-edit-discussion-rte" />
          </Field>

          <Field label="Discussion Board URL (optional)"
                 hint="Link to the external discussion (Teams/Slack/Canvas/Discord channel). Opens in a new tab.">
            <input className="input-field" value={form.discussion_url}
                   onChange={(e) => set('discussion_url', e.target.value)}
                   placeholder="https://teams.microsoft.com/… or https://canvas…/discussion_topics/…" />
          </Field>

          <Field label="References / Reading list (optional)"
                 hint="Rich text — list citations, papers, textbook chapters, recommended readings. Use the link button for clickable references.">
            <RichTextEditor value={form.references_text} onChange={(html) => set('references_text', html)}
                            placeholder="e.g., 1. James, G., et al. (2021). An Introduction to Statistical Learning…"
                            testId="topic-edit-references-rte" />
          </Field>

          <Field label="References URL (optional)"
                 hint="Optional single link — library guide, reading list, Zotero/Google Drive folder, etc.">
            <input className="input-field" value={form.references_url}
                   onChange={handleUrlChange('references_url', 'pdf')}
                   placeholder="https://library.example.edu/…" />
            {hints.references_url && <div className="text-xs text-amber-700 mt-1">{hints.references_url}</div>}
            <UploadButton accept=".pdf,.docx,.doc,.md" label="References File"
                          onUploaded={(url) => { set('references_url', url); }} />
          </Field>

          <Field label="Quiz URL (Microsoft Forms embed)"
                 hint='MS Forms → Collect responses → "Anyone can respond" → Embed → copy iframe src.'>
            <input className="input-field" value={form.quiz_url}
                   onChange={handleUrlChange('quiz_url', 'quiz')}
                   placeholder="https://forms.office.com/…&embed=true" />
            {hints.quiz_url && <div className="text-xs text-amber-700 mt-1">{hints.quiz_url}</div>}
          </Field>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-outline" data-testid="topic-edit-cancel-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="topic-edit-save-btn">
            {saving ? 'Saving…' : (isNew ? 'Create Topic' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicEditModal;
